import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "src/hooks/cn";

interface Props {
    items: {
        wizards: { type: string | null };
        subscriptions: { type: string | null };
        tripods: { type: string | null };
        accessories: { type: string | null };
        deliveryBillingDetails: { emailAddress: string | null };
        paymentDetails: { cardNumber: string | null };
    };
    step: number;
    setStep: Dispatch<SetStateAction<number>>;
}

const CheckoutSteps = ({ items, step, setStep }: Props) => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [lineWidth, setLineWidth] = useState(0);
    const [lineOffset, setLineOffset] = useState(0);

    const steps = [
        {
            id: 1,
            key: "plans",
            condition: items.subscriptions.type !== null,
            icon: (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                </svg>
            ),
        },
        {
            id: 2,
            key: "wizard_cam",
            condition: items.wizards.type !== null,
            icon: (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                </svg>
            ),
        },
        {
            id: 3,
            key: "tripods",
            condition: items.tripods.type !== null,
            icon: (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                </svg>
            ),
        },
        {
            id: 4,
            key: "accesories",
            condition: items.accessories.type !== null,
            icon: (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                </svg>
            ),
        },
        {
            id: 5,
            key: "shipment",
            condition: items.deliveryBillingDetails.emailAddress !== null,
            icon: (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h2a2 2 0 012 2v1m-6 0h6m-6 0l-2 9a2 2 0 002 2h8a2 2 0 002-2l-2-9m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v.01"
                    />
                </svg>
            ),
        },
        {
            id: 6,
            key: "payment",
            condition: items.paymentDetails.cardNumber !== null,
            icon: (
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                </svg>
            ),
        },
    ];

    // Calculate line position based on step circles
    useEffect(() => {
        const updateLinePosition = () => {
            if (containerRef.current && stepRefs.current.length === steps.length) {
                const firstStep = stepRefs.current[0];
                const lastStep = stepRefs.current[steps.length - 1];
                const currentStep = stepRefs.current[step - 1];

                if (firstStep && lastStep && currentStep) {
                    const firstRect = firstStep.getBoundingClientRect();
                    const lastRect = lastStep.getBoundingClientRect();
                    const currentRect = currentStep.getBoundingClientRect();
                    const containerRect = containerRef.current.getBoundingClientRect();

                    const startX = firstRect.left + firstRect.width / 2 - containerRect.left;
                    const currentX = currentRect.left + currentRect.width / 2 - containerRect.left;
                    const endX = lastRect.left + lastRect.width / 2 - containerRect.left;

                    setLineOffset(startX);
                    setLineWidth(currentX - startX);
                }
            }
        };

        updateLinePosition();
        window.addEventListener('resize', updateLinePosition);

        // Small delay to ensure DOM is fully rendered
        const timeout = setTimeout(updateLinePosition, 100);

        return () => {
            window.removeEventListener('resize', updateLinePosition);
            clearTimeout(timeout);
        };
    }, [step, steps.length]);

    return (
        <div className="w-full bg-white border-b border-gray-100">
            <div className="max-w-6xl mx-auto px-3 py-5 sm:px-4 sm:py-6 md:px-8">
                {/* Steps Container */}
                <div className="relative overflow-x-auto overflow-y-visible pt-4 pb-4" ref={containerRef}>
                    {/* Background Progress Line - Full width line behind everything */}
                    <div className="absolute top-10 left-0 right-0 h-0.5 bg-gray-200 z-0" />

                    {/* Active Progress Line - Follows the step circles exactly */}
                    <div
                        className="absolute top-10 h-0.5 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 z-0 transition-all duration-700 ease-out"
                        style={{
                            left: `${lineOffset}px`,
                            width: `${lineWidth}px`,
                        }}
                    />

                    {/* Steps Grid */}
                    <div className="grid grid-cols-6 gap-2 relative z-10 min-w-[720px]">
                        {steps.map(({ id, key, condition, icon }, index) => {
                            const isActive = step === id;
                            const isCompleted = step > id;
                            const isDisabled = !condition && !isCompleted;
                            const isUpcoming = !isActive && !isCompleted && condition;

                            return (
                                <div
                                    key={id}
                                    //@ts-ignore
                                    ref={(el) => (stepRefs.current[index] = el)}
                                    className="flex flex-col items-center group cursor-pointer"
                                    onClick={() => condition && setStep(id)}
                                >
                                    {/* Step Circle */}
                                    <div className="relative mb-3">
                                        <div
                                            className={cn(
                                                "w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 relative overflow-hidden",
                                                "focus:outline-none focus:ring-4 focus:ring-blue-100",
                                                isActive &&
                                                "border-blue-500 bg-blue-50 shadow-lg scale-110",
                                                isCompleted &&
                                                "border-blue-500 bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg",
                                                isUpcoming &&
                                                "border-gray-300 bg-white hover:border-blue-400 hover:shadow-md hover:scale-105",
                                                isDisabled &&
                                                "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
                                            )}
                                        >
                                            {/* Animated Background for Active Step */}
                                            {isActive && (
                                                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 opacity-10 animate-pulse" />
                                            )}

                                            {/* Step Content */}
                                            <div
                                                className={cn(
                                                    "flex items-center justify-center transition-all duration-200",
                                                    isActive && "text-blue-600",
                                                    isCompleted && "text-white",
                                                    isUpcoming &&
                                                    "text-gray-500 group-hover:text-blue-500",
                                                    isDisabled && "text-gray-400"
                                                )}
                                            >
                                                {isCompleted ? (
                                                    <div className="relative">
                                                        <svg
                                                            className="w-5 h-5"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={3}
                                                                d="M5 13l4 4L19 7"
                                                            />
                                                        </svg>
                                                    </div>
                                                ) : isActive ? (
                                                    <div className="relative animate-pulse">{icon}</div>
                                                ) : (
                                                    icon
                                                )}
                                            </div>
                                        </div>

                                        {/* Glow Effect for Active Step */}
                                        {isActive && (
                                            <div className="absolute inset-0 rounded-full bg-blue-400 opacity-20 blur-md scale-150 animate-pulse" />
                                        )}
                                    </div>

                                    {/* Step Label */}
                                    <div className="text-center max-w-[92px]">
                                        <span
                                            className={cn(
                                                "text-xs font-medium transition-all duration-200 block leading-tight",
                                                isActive && "text-blue-600 font-semibold",
                                                isCompleted && "text-gray-700 font-medium",
                                                isUpcoming && "text-gray-500 group-hover:text-gray-700",
                                                isDisabled && "text-gray-400"
                                            )}
                                        >
                                            {t(key)}
                                        </span>

                                        {/* Status Indicator */}
                                        <div className="mt-1 flex justify-center">
                                            {isCompleted && (
                                                <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse" />
                                            )}
                                            {isActive && (
                                                <div className="w-2 h-0.5 bg-blue-500 rounded-full animate-pulse" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutSteps;
